/**
 * LA11 handoff (LA-12/13/15/19): one unmounted block for LA12's existing
 * Local Node placements. LA15 supplies onDownloadPlan through the existing
 * signed-approval dispatch and feeds LA04 progress back through progress.
 * Without that callback, downloads stay disabled; planning is never dispatch.
 * The mounting owner binds the catalog's authorized device report to deviceId.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import {
  fetchLocalAiCatalog, localAiBrowseUrl, localAiLicenceUrl, planLocalAiDownload,
  recordLocalAiAcceptance, recordLocalAiFriction,
} from '../../api/localAi';
import type {
  LocalAiCatalog, LocalAiDownloadProgress, LocalAiDownloadRequest, LocalAiFrictionState, LocalAiItem,
} from '../../api/localAi';

export interface LocalAiIntakeProps {
  mode: 'setup' | 'settings';
  deviceId: string | null;
  onSkip?: () => void;
  /** LA-15: resolves only after the existing approval/dispatch owner accepts this request. */
  onDownloadPlan?: (deviceId: string, plan: LocalAiDownloadRequest) => Promise<void>;
  progress?: readonly LocalAiDownloadProgress[];
}

const CONNECTORS = [
  { id: 'resolve', label: 'DaVinci Resolve' }, { id: 'ollama', label: 'Ollama' },
  { id: 'docker', label: 'Docker' }, { id: 'comfyui', label: 'ComfyUI' },
] as const;

const CAPABILITY_LABELS: Record<string, string> = {
  text_llm: 'Text and writing', image: 'Images', video: 'Video', tts: 'Voice',
  audio_processing: 'Audio processing', music_sfx: 'Music and sound effects', transcription: 'Transcription',
  model_3d: '3D models', unreal: 'Unreal projects', resolve_connector: 'Editing in Resolve', local_builders: 'Build tools',
};

function reasonText(code: string, fallback: string): string {
  if (code === 'needs_local_node') return 'Pair a local device and bring it online to use local AI.';
  if (code === 'needs_hf_token') return 'Add your own Hugging Face token in Auteur Bridge on your computer.';
  if (code === 'not_in_your_plan') return 'Your products do not include this local capability.';
  return fallback;
}

function sizeText(bytes: number | null): string {
  if (bytes === null) return 'Size unknown';
  if (bytes < 1024) return `${bytes} bytes`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)) - 1, units.length - 1);
  return `${(bytes / 1024 ** (index + 1)).toFixed(1)} ${units[index]}`;
}

function BrowseLink({ url }: { url: string | null }) {
  const safe = localAiBrowseUrl(url);
  return safe ? <a href={safe} target="_blank" rel="noopener noreferrer">Browse Hugging Face</a> : null;
}

function ModelCard({ model, deviceId, onDownloadPlan, progress, refresh }: {
  model: LocalAiItem;
  deviceId: string | null;
  onDownloadPlan: LocalAiIntakeProps['onDownloadPlan'];
  progress: readonly LocalAiDownloadProgress[];
  refresh: () => Promise<void>;
}) {
  const controlId = useId();
  const [checked, setChecked] = useState(!model.acceptance_required && model.licence_acceptance !== null);
  const [flow, setFlow] = useState<LocalAiFrictionState | null>(null);
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState<LocalAiDownloadRequest | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const active = useRef(true);
  const running = useRef(false);
  const requestId = useRef<string | null>(null);

  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => {
    setChecked(!model.acceptance_required && model.licence_acceptance !== null);
  }, [model.acceptance_required, model.licence_acceptance?.accepted_at, model.licence_acceptance?.digest]);

  const licenceUrl = localAiLicenceUrl(model.licence?.url ?? null);
  const questionable = model.friction_required || model.licence?.tier === 'questionable' || model.licence?.tier === 'restricted';
  const frictionProof = flow ? flow.proof : model.friction_proof;
  const notice = flow?.notice ?? model.notice;
  const confirmation = flow?.confirmation_phrase ?? model.confirmation_phrase;
  const report = plan ? progress.find((entry) => entry.request_id === plan.request_id) : undefined;
  const inProgress = report ? ['queued', 'downloading', 'verifying'].includes(report.state) : submitted;
  const metadataKnown = model.tested && model.licence?.id && licenceUrl && model.licence.version_or_date
    && model.licence.tier && model.file_ids !== null && model.store_id !== null
    && model.gated !== null && model.token_required !== null && model.mature !== null;
  const blocked = !deviceId ? { code: 'needs_local_node', text: reasonText('needs_local_node', '') }
    : model.state === 'disabled' ? { code: model.reason_code, text: reasonText(model.reason_code, model.reason_text) }
      : !metadataKnown ? { code: 'model_details_unknown', text: 'Download details are not available yet.' }
        : model.token_required && !model.token_present ? { code: 'needs_hf_token', text: reasonText('needs_hf_token', '') }
          : model.mature && (!model.adult_attested || !model.mature_attestation_ref)
            ? { code: 'adult_attestation_required', text: 'Adult attestation and confirmation of this model are required.' }
            : null;
  const downloadBlock = blocked
    ?? (!onDownloadPlan ? { code: 'download_unavailable', text: 'Downloads are not available here yet.' } : null)
    ?? (!checked || !model.licence_acceptance ? { code: 'licence_acceptance_required', text: 'Read and accept the licence before downloading.' } : null)
    ?? (questionable && !frictionProof ? { code: 'friction_required', text: 'Complete the licence review and typed confirmation first.' } : null)
    ?? (!model.download_ready ? { code: model.reason_code, text: reasonText(model.reason_code, model.reason_text) } : null);

  async function run(action: () => Promise<void>) {
    if (running.current || !active.current) return;
    running.current = true;
    setBusy(true);
    setError('');
    try {
      await action();
    } catch {
      // LA-5/10: never reflect raw transport/configuration/dispatch diagnostics.
      if (active.current) setError('This action could not be completed. Refresh the status and try again.');
    } finally {
      running.current = false;
      if (active.current) setBusy(false);
    }
  }

  async function review() {
    if (blocked) return;
    await run(async () => {
      const next = await recordLocalAiFriction({ model_id: model.item_id, action: 'show_notice' });
      if (!active.current) return;
      setFlow(next);
      setChecked(false);
      setPhrase('');
    });
  }

  async function acceptLicence(value: boolean) {
    if (blocked || !licenceUrl || !model.licence?.version_or_date || model.file_ids === null) return;
    if (!value && !questionable) {
      // LA-4: unchecked locally prevents use; the historical server acceptance
      // remains recorded. LA10 has no consent-deletion endpoint.
      setChecked(false);
      return;
    }
    await run(async () => {
      if (questionable) {
        const next = await recordLocalAiFriction({ model_id: model.item_id, action: 'check_licence', checked: value });
        if (!active.current) return;
        setFlow(next);
      }
      if (value) await recordLocalAiAcceptance({ model_id: model.item_id, file_ids: model.file_ids!,
        licence_url: licenceUrl, licence_version: model.licence!.version_or_date! });
      if (!active.current) return;
      setChecked(value);
      await refresh();
    });
  }

  async function confirm() {
    // LA-5/8: only the exact server phrase can leave this typed-confirmation
    // field. Pasting any other text cannot create a request.
    if (blocked || !checked || !confirmation || phrase !== confirmation) return;
    await run(async () => {
      const next = await recordLocalAiFriction({ model_id: model.item_id, action: 'confirm_phrase', phrase });
      if (!active.current) return;
      setFlow(next);
      await refresh();
    });
  }

  async function download() {
    if (downloadBlock || !deviceId || !onDownloadPlan || inProgress || report?.state === 'placed') return;
    await run(async () => {
      if (report?.state === 'failed' || report?.state === 'refused') requestId.current = null;
      // LA-19: an ordinary request id for the existing wire contract, reused
      // after an ambiguous submission failure; no media identity is minted.
      requestId.current ??= globalThis.crypto.randomUUID();
      const next = await planLocalAiDownload({
        request_id: requestId.current, model_id: model.item_id, file_ids: model.file_ids!, store_id: model.store_id!,
        licence_acceptance: model.licence_acceptance!, token_mode: model.token_required ? 'device_hf_token' : 'none',
        ...(questionable && frictionProof ? { friction: frictionProof } : {}),
        ...(model.mature && model.mature_attestation_ref ? { mature_attestation_ref: model.mature_attestation_ref } : {}),
      });
      if (!active.current) return;
      setPlan(next);
      await onDownloadPlan(deviceId, next);
      if (active.current) setSubmitted(true);
    });
  }

  // LA-11: no name, link, licence, consent or action before legal attestation.
  if (model.mature && !model.adult_attested) {
    return <article className="settings-card" aria-disabled="true">
      <p data-reason-code={blocked?.code ?? model.reason_code}>
        {blocked?.text ?? reasonText(model.reason_code, model.reason_text)}
      </p>
    </article>;
  }

  const progressLabels = { queued: 'Waiting for your computer', downloading: 'Downloading', verifying: 'Checking the download',
    placed: 'Download complete', failed: 'Download failed', refused: 'Download could not start' };

  return <article className="settings-card" data-model-id={model.item_id} data-state={model.state}>
    <h6>{model.item_id}{model.installed ? ' · Found on your computer' : ''}</h6>
    <p>{sizeText(model.size_bytes)}</p>
    <p>Licence: {model.licence?.id ?? 'Unknown'}</p>
    {licenceUrl ? <p><a href={licenceUrl} target="_blank" rel="noopener noreferrer">{licenceUrl}</a></p> : <p>Licence link unavailable.</p>}
    {model.licence?.summary ? <p>{model.licence.summary}</p> : null}
    <p data-reason-code={blocked?.code ?? model.reason_code}>{blocked?.text ?? reasonText(model.reason_code, model.reason_text)}</p>

    {/* LA-8: verbatim server notice; no local notice or model-specific exception. */}
    {questionable ? <div className="notice">
      {notice ? <p style={{ whiteSpace: 'pre-wrap' }}>{notice}</p> : <p>The licence notice is not available yet.</p>}
      <button type="button" className="secondary" disabled={busy || !!blocked || !notice || !confirmation}
        onClick={() => void review()}>Review licence</button>
    </div> : null}

    <label htmlFor={`${controlId}-accept`}>
      <input id={`${controlId}-accept`} type="checkbox" checked={checked}
        disabled={busy || !!blocked || !licenceUrl || (questionable && (!flow || flow.stage === 'confirmed'))}
        onChange={(event) => void acceptLicence(event.target.checked)} />
      {questionable ? 'I have checked and accept the licence for my location' : 'I have read and accept this licence'}
    </label>

    {questionable ? <div>
      <label htmlFor={`${controlId}-confirmation`}>Type {confirmation ?? 'the model name shown after review'} to confirm</label>
      <input id={`${controlId}-confirmation`} type="text" autoComplete="off" value={phrase}
        disabled={busy || !!blocked || !checked || flow?.stage !== 'licence_checked'}
        onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }}
        onChange={(event) => setPhrase(event.target.value)} />
      <button type="button" className="secondary"
        disabled={busy || !!blocked || !checked || !confirmation || phrase !== confirmation || flow?.stage !== 'licence_checked'}
        onClick={() => void confirm()}>Confirm licence review</button>
    </div> : null}

    {/* LA-5: no token input. This command takes the user's token only on their own computer. */}
    {model.gated || model.token_required ? <div>
      <p>On your computer, open Auteur Bridge and run <code>auteur-bridge ai set-hf-token</code>.
        Enter your own Hugging Face token there when asked, then refresh this status.</p>
      <p>Hugging Face token on your computer: {model.token_present ? 'Present' : 'Not present'}</p>
    </div> : null}

    {/* LA-3/8: a questionable entry has no Download control until server confirmation. */}
    {!questionable || frictionProof ? <button type="button"
      disabled={busy || !!downloadBlock || inProgress || report?.state === 'placed'}
      onClick={() => void download()}>Download</button> : null}
    {downloadBlock ? <p data-reason-code={downloadBlock.code}>{downloadBlock.text}</p> : null}
    {error ? <p className="error" role="alert">{error}</p> : null}
    <div aria-live="polite">
      {report ? <>
        <p data-reason-code={report.reason_code ?? undefined}>{progressLabels[report.state]}</p>
        {model.size_bytes !== null && model.size_bytes > 0
          ? <progress aria-label={`Download progress for ${model.item_id}`} max={model.size_bytes} value={Math.min(report.bytes_done, model.size_bytes)} />
          : report.state === 'downloading' || report.state === 'verifying' ? <progress aria-label={`Download progress for ${model.item_id}`} /> : null}
        <p>{sizeText(report.bytes_done)} received</p>
      </> : <p>{busy ? 'Checking your request…' : submitted ? 'Download requested. Waiting for your computer.' : 'No download started.'}</p>}
    </div>
    <BrowseLink url={model.browse_url} />
  </article>;
}

function IntakeContent({ deviceId, onDownloadPlan, progress = [], skipped }: Omit<LocalAiIntakeProps, 'mode' | 'onSkip'> & { skipped: boolean }) {
  const [catalog, setCatalog] = useState<LocalAiCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const active = useRef(true);
  const latestRead = useRef(0);
  const refresh = useCallback(async () => {
    const read = ++latestRead.current;
    setLoading(true);
    try {
      const next = await fetchLocalAiCatalog();
      if (active.current && read === latestRead.current) { setCatalog(next); setError(false); }
    } catch (failure) {
      if (active.current && read === latestRead.current) { setCatalog(null); setError(true); }
      throw failure;
    } finally {
      if (active.current && read === latestRead.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    active.current = true;
    void refresh().catch(() => {});
    return () => { active.current = false; latestRead.current += 1; };
  }, [refresh]);

  const capabilities = catalog?.items.filter((item) => item.kind === 'capability') ?? [];
  // LA-11: defense in depth over the server's child projection, before grouping.
  const models = catalog?.items.filter((item) => item.kind === 'model' && !(catalog.context_kind === 'child' && item.mature)) ?? [];

  return <>
    {/* LA-7/10: four rows even during loading, errors, empty plans or Skip. */}
    <div className="settings-card-list" aria-label="Local connectors">
      {CONNECTORS.map(({ id, label }) => {
        const row = catalog?.items.find((item) => item.kind === 'connector' && item.item_id === id);
        const code = !deviceId ? 'needs_local_node' : row?.reason_code ?? 'connector_status_unknown';
        return <article key={id} className="settings-card" aria-disabled={!deviceId || !row || row.state === 'disabled'}>
          <h5>{label}</h5>
          <p>{!deviceId || row?.state === 'disabled' ? 'Unavailable' : row ? 'Connected' : 'Status unknown'}</p>
          <p data-reason-code={code}>{reasonText(code, row?.reason_text ?? 'This connection could not be checked yet.')}</p>
        </article>;
      })}
    </div>
    {error ? <p className="error" role="alert">Local AI status could not be loaded. Try again.</p> : null}
    {loading ? <p role="status">Checking local AI…</p> : null}
    <button type="button" className="secondary" disabled={loading} onClick={() => void refresh().catch(() => {})}>Refresh status</button>
    {!skipped ? <>
      {capabilities.map((capability) => <section key={capability.item_id} className="settings-subsection"
        aria-label={CAPABILITY_LABELS[capability.item_id] ?? capability.item_id}>
        <h5>{CAPABILITY_LABELS[capability.item_id] ?? capability.item_id}</h5>
        <p data-reason-code={!deviceId ? 'needs_local_node' : capability.reason_code}>
          {!deviceId ? reasonText('needs_local_node', '') : reasonText(capability.reason_code, capability.reason_text)}
        </p>
        <div className="settings-card-list">
          {models.filter((model) => model.tested && model.capabilities.includes(capability.item_id)).map((model) =>
            <ModelCard key={JSON.stringify([model.item_id, model.connector, model.licence, model.file_ids, model.store_id,
              model.notice, model.adult_attested, model.mature_attestation_ref])}
              model={model} deviceId={deviceId} onDownloadPlan={onDownloadPlan} progress={progress} refresh={refresh} />)}
        </div>
        <BrowseLink url={capability.browse_url} />
      </section>)}
      {models.filter((model) => !model.tested).map((model) => <article className="settings-card" key={`${model.item_id}:${model.connector}`}>
        <h5>{model.item_id}{model.installed ? ' · Found on your computer' : ''}</h5>
        <p data-reason-code={!deviceId ? 'needs_local_node' : model.reason_code}>
          {!deviceId ? reasonText('needs_local_node', '') : reasonText(model.reason_code, model.reason_text)}
        </p>
        <BrowseLink url={model.browse_url} />
      </article>)}
    </> : null}
  </>;
}

export function LocalAiIntake({ mode, deviceId, onSkip, onDownloadPlan, progress }: LocalAiIntakeProps) {
  const [skipped, setSkipped] = useState(false);
  const showSkipped = mode === 'setup' && skipped;
  return <section className="settings-panel-content" aria-label="Local AI">
    <div className="settings-card-heading">
      <h4>Local AI</h4>
      {/* LA-12: optional inside the existing block, including loading and errors. */}
      {mode === 'setup' ? <button type="button" className="secondary" onClick={() => { setSkipped(true); onSkip?.(); }}>Skip</button> : null}
    </div>
    {showSkipped ? <p>You can set up local AI later in Settings. <button type="button" className="secondary"
      onClick={() => setSkipped(false)}>Set up local AI</button></p> : null}
    <IntakeContent key={deviceId ?? ''} deviceId={deviceId} onDownloadPlan={onDownloadPlan} progress={progress} skipped={showSkipped} />
  </section>;
}
